/** <slide-show>: generic Swiper wrapper, mobile-first — bare data-* are the desktop config, data-mobile-* apply below the breakpoint, and the `loaded` class reveals the slider without a FOUC. */

/** Swiper (~150 KB) is injected on the first real slider init rather than loaded globally, so pages without an active slider pay nothing; the asset URL comes from window.theme.swiperJS since `asset_url` is unavailable in a .js file. */
let swiperLoadPromise = null;
function loadSwiper() {
  if (typeof Swiper !== "undefined") return Promise.resolve();
  if (swiperLoadPromise) return swiperLoadPromise;

  const src = (window.theme && window.theme.swiperJS) || null;
  if (!src) {
    return Promise.reject(new Error("[slide-show] URL Swiper absente (window.theme.swiperJS)"));
  }

  swiperLoadPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.addEventListener("load", () => resolve());
    s.addEventListener("error", reject);
    document.head.appendChild(s);
  });
  return swiperLoadPromise;
}

class Slideshow extends HTMLElement {
  constructor() {
    super();
    const d = this.dataset;

    // Slides par vue
    this.nbPerSlide = d.nbPerSlide || 1;
    this.mobileNbPerSlide = d.mobileNbPerSlide || 1;

    // Espacement
    this.spaceBetween = d.spaceBetween || 24;
    this.mobileSpaceBetween = d.mobileSpaceBetween || this.spaceBetween;

    // Activations par couche
    this.navigation = d.navigation === "true";
    this.mobileNavigation = d.mobileNavigation === "true";
    this.pagination = d.pagination === "true";
    this.mobilePagination = d.mobilePagination === "true";
    this.scrollbar = d.scrollbar === "true";
    this.mobileScrollbar = d.mobileScrollbar === "true";
    this.loop = d.loop === "true";
    this.mobileLoop = d.mobileLoop === "true";
    this.freeMode = d.freeMode === "true";
    this.mobileFreeMode = d.mobileFreeMode === "true";

    // Autres
    this.autoplay = d.autoplay === "true";
    this.autoplayDelay = parseInt(d.autoplayDelay) || 3000;
    this.breakpoint = parseInt(d.breakpoint) || 768;
    this.speed = parseInt(d.speed) || 300;
    this.direction = d.direction === "vertical" ? "vertical" : "horizontal";
    this.effect = d.effect && d.effect !== "slide" ? d.effect : null;
    // Space after the last slide (px), mobile layer only.
    this.mobileOffsetAfter = parseFloat(d.mobileOffsetAfter) || 0;

    // Slider mobile uniquement : au-dessus de `breakpoint`, aucun Swiper.
    this.mobileOnly = d.mobileOnly === "true";
    this.desktopOnly = d.desktopOnly === "true";
  }

  connectedCallback() {
    if (this._initialized) return;

    // Pointer-only slider (product card hover): on a touch device Swiper is not instantiated, the raw markup showing the first slide only.
    if (this.dataset.disableOnTouch === "true" && !window.matchMedia("(hover: hover)").matches) {
      return;
    }

    // Mobile only: init/destroy on breakpoint crossing, `destroy(true, true)` clearing the inline styles and classes so the raw markup re-stacks in CSS.
    if (this.mobileOnly || this.desktopOnly) {
      const query = this.desktopOnly ? `(min-width: ${this.breakpoint}px)` : `(max-width: ${this.breakpoint - 1}px)`;
      this._mq = window.matchMedia(query);
      this._onMq = () => this.#syncBreakpoint();
      this._mq.addEventListener("change", this._onMq);
      this.#syncBreakpoint();
      return;
    }

    this.#waitForSwiperThen(() => this.initializeSlideshow());
  }

  disconnectedCallback() {
    if (this._mq && this._onMq) this._mq.removeEventListener("change", this._onMq);
    this._swiper?.destroy(true, true);
    this._swiper = null;
    this._initialized = false;
  }

  /** Aligne l'état du slider sur le breakpoint (modes `data-mobile-only` / `data-desktop-only`). */
  #syncBreakpoint() {
    if (this._mq.matches) {
      if (this._initialized) return;
      // Chargement de Swiper asynchrone : re-vérifier le breakpoint au retour,
      // l'utilisateur a pu repasser en desktop entre-temps.
      this.#waitForSwiperThen(() => {
        if (this._mq.matches && !this._initialized) this.initializeSlideshow();
      });
      return;
    }

    if (!this._swiper) return;
    this._swiper.destroy(true, true);
    this._swiper = null;
    this._initialized = false;
    this.classList.remove("loaded");
  }

  /** Loads Swiper on demand (once, shared) then initialises the slider, or inits straight away if Swiper is already there. */
  #waitForSwiperThen(cb) {
    if (typeof Swiper !== "undefined") return cb();
    loadSwiper()
      .then(() => {
        if (typeof Swiper !== "undefined") cb();
        else console.warn("[slide-show] Swiper indisponible après chargement", this);
      })
      .catch((err) => {
        console.warn("[slide-show] échec du chargement de Swiper", err, this);
      });
  }

  #parseSpv(v) {
    return v === "auto" ? "auto" : parseFloat(v);
  }

  initializeSlideshow() {
    const swiperEl = this.querySelector(".swiper");
    if (!swiperEl) return;
    this._initialized = true;

    // Recherche des éléments de nav/pagination. Si absents, on n'ajoute pas
    // la clé au config - sinon Swiper crashe sur `null.querySelectorAll`.
    const navNext = this.querySelector(".slideshow_button_next");
    const navPrev = this.querySelector(".slideshow_button_prev");
    const pagEl = this.querySelector(".swiper-pagination");
    const scrollbarEl = this.querySelector(".swiper-scrollbar");

    const desktopBreakpoint = {
      slidesPerView: this.#parseSpv(this.nbPerSlide),
      spaceBetween: parseFloat(this.spaceBetween),
      loop: this.loop,
      freeMode: this.freeMode,
    };

    const config = {
      // Couche mobile (defaults Swiper)
      slidesPerView: this.#parseSpv(this.mobileNbPerSlide),
      spaceBetween: parseFloat(this.mobileSpaceBetween),
      loop: this.mobileLoop,
      freeMode: this.mobileFreeMode,
      speed: this.speed,
      direction: this.direction,
      watchOverflow: true,

      breakpoints: { [this.breakpoint]: desktopBreakpoint },

      on: {
        init: () => {
          this.classList.add("loaded");
          this.dispatchEvent(
            new CustomEvent("slide-show:ready", {
              detail: { swiper: this._swiper },
              bubbles: true,
            }),
          );
        },
      },
    };

    if (navNext || navPrev) {
      config.navigation = {
        nextEl: navNext,
        prevEl: navPrev,
        enabled: this.mobileNavigation,
      };
      desktopBreakpoint.navigation = { enabled: this.navigation };
    }

    if (pagEl) {
      config.pagination = {
        el: pagEl,
        clickable: true,
        enabled: this.mobilePagination,
      };
      desktopBreakpoint.pagination = { enabled: this.pagination };
    }

    if (scrollbarEl) {
      config.scrollbar = {
        el: scrollbarEl,
        draggable: true,
        enabled: this.mobileScrollbar,
      };
      desktopBreakpoint.scrollbar = { enabled: this.scrollbar };
    }

    // Thumbs : link à un autre <slide-show> par sélecteur CSS. L'autre
    // doit être DÉJÀ initialisé (= venir avant ce slider dans le DOM).
    if (this.dataset.thumbs) {
      const thumbsEl = document.querySelector(this.dataset.thumbs);
      const thumbsInstance = thumbsEl?.swiper;
      if (thumbsInstance) {
        config.thumbs = { swiper: thumbsInstance };
      } else if (thumbsEl) {
        console.warn("[slide-show] thumbs target trouvé mais pas encore initialisé. Place le slide-show des thumbs AVANT celui-ci dans le DOM.", this);
      }
    }

    // Touches starting on these elements (e.g. a nested slider) never drag this one.
    if (this.dataset.noSwipingSelector) {
      config.noSwipingSelector = this.dataset.noSwipingSelector;
    }

    if (this.mobileOffsetAfter) {
      config.slidesOffsetAfter = this.mobileOffsetAfter;
      desktopBreakpoint.slidesOffsetAfter = 0;
    }

    if (this.effect) {
      config.effect = this.effect;
      // crossFade évite de voir le fond à travers pendant la transition fade.
      if (this.effect === "fade") config.fadeEffect = { crossFade: true };
    }

    if (this.autoplay) {
      config.autoplay = {
        delay: this.autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: !this.mobileOnly,
      };
    }

    this._swiper = new Swiper(swiperEl, config);
  }

  restart() {
    const sw = this._swiper;
    if (!sw) return;
    sw.animating = false;
    sw.update();
    if (this.autoplay && sw.autoplay) {
      sw.autoplay.stop();
      sw.autoplay.start();
    }
  }

  /** Accès public à l'instance Swiper (null tant que pas init). */
  get swiper() {
    return this._swiper || null;
  }
}

if (!customElements.get("slide-show")) {
  customElements.define("slide-show", Slideshow);
}

class ShippingDate extends HTMLElement {
  constructor() {
    super();
    this.onVariantChangeUnsubscriber = null;
  }

  connectedCallback() {
    this.shippingSpan = this.querySelector(".shipping_date");
    this.shippingDiffereSpan = this.querySelector(".shipping_date_differe");
    this.line = this.querySelector(".estimated_shipping");
    this.productInfo = this.closest("product-info");

    // Calcul initial
    this.refreshVisibilityAndDate();

    // Refresh à chaque changement de variante (robuste, même si le DOM ne remplace pas le bloc)
    if (typeof subscribe === "function" && this.productInfo) {
      this.onVariantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        // Ne réagit que pour la même section produit
        if (data?.sectionId !== this.productInfo.sectionId) return;
        this.refreshVisibilityAndDate(data?.variant);
      });
    }
  }

  disconnectedCallback() {
    this.onVariantChangeUnsubscriber?.();
  }

  refreshVisibilityAndDate(variant = null) {
    // Si on reçoit la variante et qu'elle est indispo => cacher
    if (variant && !variant.available) {
      this.line?.classList.add("hidden");
      return;
    }

    // Sinon afficher + recalculer
    this.line?.classList.remove("hidden");
    this.calculerLivraisonEstimee();
    this.formaterDateDifferee();
  }

  calculerLivraisonEstimee() {
    if (!this.shippingSpan) return;

    const maintenant = new Date();
    let joursOuvrables = 2;

    if (maintenant.getDay() === 4) {
      // jeudi
      joursOuvrables = maintenant.getHours() < 16 ? 2 : 5;
    } else if (maintenant.getDay() === 5) {
      // vendredi
      joursOuvrables = 5;
    } else if (maintenant.getDay() === 6) {
      // samedi
      joursOuvrables = 4;
    } else if (maintenant.getDay() === 0) {
      // dimanche
      joursOuvrables = 3;
    }

    const dateLivraisonEstimee = new Date(maintenant.getTime() + joursOuvrables * 24 * 60 * 60 * 1000);
    const options = { day: "numeric", month: "short" };
    this.shippingSpan.textContent = dateLivraisonEstimee.toLocaleDateString(document.documentElement.lang || "fr", options);
  }

  formaterDateDifferee() {
    if (!this.shippingDiffereSpan) return;
    const startTs = parseInt(this.shippingDiffereSpan.dataset.dateStart) * 1000;
    const endTs = parseInt(this.shippingDiffereSpan.dataset.dateEnd) * 1000;
    if (isNaN(startTs) || isNaN(endTs)) return;

    const fmt = (ts) => {
      const d = new Date(ts);
      return d.toLocaleDateString(document.documentElement.lang || "fr", { day: "numeric", month: "short" });
    };
    const startStr = fmt(startTs);
    const endStr = fmt(endTs);

    const dates = [startStr, endStr];
    let i = 0;
    this.shippingDiffereSpan.textContent = this.shippingDiffereSpan.textContent.replace(/\d{2}\/\d{2}\/\d{4}/g, () => dates[i++]);
  }
}

customElements.define("shipping-date", ShippingDate);

/** PDP reassurance messages: shows one slide at a time, skips slides whose children are all `hidden` (unavailable variant), pauses on hover/focus. */
class ReinsuranceTicker extends HTMLElement {
  constructor() {
    super();
    this.timer = null;
    this.paused = false;
    this.pause = () => (this.paused = true);
    this.resume = () => (this.paused = false);
  }

  connectedCallback() {
    this.slides = [...this.querySelectorAll(":scope > .main_product_reinsurance__slide")];
    if (this.slides.length < 2 || this.timer) return;

    this.addEventListener("mouseenter", this.pause);
    this.addEventListener("mouseleave", this.resume);
    this.addEventListener("focusin", this.pause);
    this.addEventListener("focusout", this.resume);

    const current = this.slides.find((slide) => slide.classList.contains("is-active"));
    if (!current || this.isEmpty(current)) this.next(true);
    this.timer = setInterval(() => this.next(), Number(this.dataset.interval) || 2500);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    this.timer = null;
  }

  isEmpty(slide) {
    return ![...slide.children].some((child) => !child.hidden);
  }

  next(force = false) {
    if (this.paused && !force) return;
    const current = this.slides.find((slide) => slide.classList.contains("is-active"));
    const index = this.slides.indexOf(current);

    for (let step = 1; step <= this.slides.length; step++) {
      const candidate = this.slides[(index + step) % this.slides.length];
      if (candidate === current || this.isEmpty(candidate)) continue;

      if (current) {
        current.classList.remove("is-active");
        current.classList.add("is-leaving");
        current.setAttribute("aria-hidden", "true");
        setTimeout(() => current.classList.remove("is-leaving"), 400);
      }
      candidate.classList.remove("is-leaving");
      candidate.classList.add("is-active");
      candidate.removeAttribute("aria-hidden");
      return;
    }
  }
}

if (!customElements.get("reinsurance-ticker")) {
  customElements.define("reinsurance-ticker", ReinsuranceTicker);
}

/** Legal pages summary: builds a clickable table of contents in #summary_aside from the `data-legals-title` headings, scrolls smoothly through Lenis when present, marks the current link `is-active`, and drops the empty paragraphs left by the Shopify rich editor. */
(function () {
  function initLegalsSummary() {
    const aside = document.getElementById("summary_aside");
    if (!aside) return;

    const content = document.querySelector(".legals_main_content") || document;

    // Purge des paragraphes vides (nbsp, espaces, <br> seul), hors images.
    content.querySelectorAll("p").forEach((p) => {
      const text = (p.textContent || "").replace(/\s/g, "").trim();
      if (text === "" && !p.querySelector("img")) {
        p.remove();
      }
    });

    // Summary is desktop only (> 750px), where the aside is visible; re-evaluated on breakpoint change so it appears without a reload.
    const desktop = window.matchMedia("(min-width: 751px)");

    function buildSummary() {
      if (!desktop.matches) return; // mobile : rien à faire
      if (aside.childElementCount > 0) return; // déjà construit

      const titles = Array.from(content.querySelectorAll('[data-legals-title="true"]'));
      if (titles.length === 0) return;

      // Décalage pour ne pas masquer le titre derrière le header fixe.
      function headerOffset() {
        const raw = getComputedStyle(document.documentElement).getPropertyValue("--header-height");
        const h = parseInt(raw, 10);
        return (isNaN(h) ? 60 : h) + 24;
      }

      // Défilement doux vers un titre (Lenis si dispo, sinon natif).
      function scrollToTitle(target) {
        const offset = headerOffset();
        if (window.lenis && typeof window.lenis.scrollTo === "function") {
          window.lenis.scrollTo(target, { offset: -offset });
        } else {
          const top = target.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top, behavior: "smooth" });
        }
      }

      // Construction du sommaire.
      const nav = document.createElement("nav");
      nav.className = "legals_summary";
      nav.setAttribute("aria-label", "Sommaire");

      const list = document.createElement("ul");
      list.className = "legals_summary_list";

      const links = [];

      titles.forEach((title, index) => {
        const text = (title.textContent || "").trim();
        if (!text) return;

        // Garantit un id d'ancre unique sur le titre.
        if (!title.id) {
          title.id = "legals-title-" + (index + 1);
        }

        const li = document.createElement("li");
        li.className = "legals_summary_item";

        const link = document.createElement("a");
        link.className = "legals_summary_link";
        link.href = "#" + title.id;
        link.textContent = text;

        link.addEventListener("click", (e) => {
          e.preventDefault();
          scrollToTitle(title);
          // Met à jour l'URL sans provoquer le saut natif (qui doublerait Lenis).
          if (history.replaceState) {
            history.replaceState(null, "", "#" + title.id);
          }
        });

        li.appendChild(link);
        list.appendChild(li);
        links.push({ link, title });
      });

      if (links.length === 0) return;

      nav.appendChild(list);
      aside.appendChild(nav);

      // Highlight: one active link at a time — the last heading whose top crossed the header line, the first item staying underlined until then; computed on scroll, throttled on a rAF.
      let activeLink = null;

      function setActive(link) {
        if (link === activeLink) return;
        if (activeLink) activeLink.classList.remove("is-active");
        if (link) link.classList.add("is-active");
        activeLink = link;
      }

      function updateActive() {
        // 1px de tolérance : évite qu'un arrondi sous-pixel désactive le lien
        // juste après un scrollTo calé pile sur la ligne.
        const line = headerOffset() + 1;
        let current = links[0].link;
        links.forEach((l) => {
          if (l.title.getBoundingClientRect().top <= line) current = l.link;
        });
        setActive(current);
      }

      let scheduled = false;
      function scheduleUpdate() {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => {
          scheduled = false;
          updateActive();
        });
      }

      window.addEventListener("scroll", scheduleUpdate, { passive: true });
      window.addEventListener("resize", scheduleUpdate);
      updateActive();
    }

    buildSummary();
    desktop.addEventListener("change", buildSummary);

    initLegalsAccordions(content, desktop);
  }

  /** Mobile accordions for legal pages: the rich-editor content is a flat block sequence, so each `data-legals-title` becomes an <accordion-component> trigger at load, built on mobile ONLY (desktop relies on sibling selectors that would stop matching) and grouped by real parent since it varies per page. */
  function initLegalsAccordions(content, desktop) {
    let snapshot = null; // [{ parent, nodes }] — ordre d'origine, pour défaire.

    function icon() {
      const tpl = document.getElementById("legals_accordion_icon");
      return tpl ? tpl.content.cloneNode(true) : null;
    }

    function build() {
      if (snapshot) return; // déjà construit
      const titles = Array.from(content.querySelectorAll('[data-legals-title="true"]'));
      if (titles.length === 0) return;

      const byParent = new Map();
      titles.forEach((title) => {
        const parent = title.parentElement;
        if (!parent) return;
        if (!byParent.has(parent)) byParent.set(parent, []);
        byParent.get(parent).push(title);
      });

      snapshot = [];

      byParent.forEach((parentTitles, parent) => {
        // Snapshot AVANT déplacement : suffit à tout remettre en place.
        snapshot.push({ parent, nodes: Array.from(parent.childNodes) });

        parentTitles.forEach((title) => {
          const accordion = document.createElement("accordion-component");
          accordion.className = "legals_accordion";
          // All sections closed at load, the state set before the custom element upgrade so nothing transitions on load.
          accordion.dataset.open = "false";
          // Mode exclusif : ouvrir une section referme les autres.
          accordion.dataset.accordionGroup = "legals";

          const trigger = document.createElement("button");
          trigger.type = "button";
          trigger.className = "accordion__trigger legals_accordion__trigger";
          trigger.setAttribute("aria-expanded", "false");

          const panel = document.createElement("div");
          panel.className = "accordion__panel";
          const inner = document.createElement("div");
          inner.className = "accordion__panel-inner legals_accordion__content";
          panel.appendChild(inner);

          // The accordion takes the heading's place, then the heading itself moves into the button, keeping its id (desktop summary anchors), its data-legals-title and its styles.
          parent.insertBefore(accordion, title);
          trigger.appendChild(title);
          const toggle = icon();
          if (toggle) trigger.appendChild(toggle);
          accordion.appendChild(trigger);
          accordion.appendChild(panel);

          // Tout ce qui suit jusqu'au prochain titre part dans le panneau.
          let node = accordion.nextSibling;
          while (node) {
            const next = node.nextSibling;
            if (node.nodeType === 1) {
              // On s'arrête aussi sur un bloc qui CONTIENT un titre, pour ne
              // pas avaler la section suivante si le balisage est imbriqué.
              if (node.matches('[data-legals-title="true"]') || node.querySelector('[data-legals-title="true"]')) {
                break;
              }
            }
            inner.appendChild(node);
            node = next;
          }
        });
      });
    }

    function destroy() {
      if (!snapshot) return;
      snapshot.forEach((entry) => {
        // Re-appended in the original order: appendChild moves nodes, so the initial sequence (blocks and text nodes) is restored as is, and the emptied wrappers are removed after.
        entry.nodes.forEach((node) => entry.parent.appendChild(node));
        entry.parent.querySelectorAll(":scope > accordion-component.legals_accordion").forEach((el) => el.remove());
      });
      snapshot = null;
    }

    function sync() {
      if (desktop.matches) destroy();
      else build();
    }

    sync();
    desktop.addEventListener("change", sync);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initLegalsSummary);
  } else {
    initLegalsSummary();
  }
})();

/** Orbe country/language dropdowns and AccessiWay's <access-widget-ui> hosts are injected by third-party scripts: each gets data-lenis-prevent as soon as it appears, so it scrolls natively under Lenis. */
(function () {
  const SELECTOR = ".md-form__select__language__list, .md-form__select__country__list, access-widget-ui";

  function mark(root) {
    if (root.matches?.(SELECTOR)) root.setAttribute("data-lenis-prevent", "");
    root.querySelectorAll?.(SELECTOR).forEach((list) => list.setAttribute("data-lenis-prevent", ""));
  }

  function init() {
    mark(document.body);
    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === Node.ELEMENT_NODE) mark(node);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
